import React, { useState } from "react";
import { Package as Caja, Phone, Check, Search } from "lucide-react";
import { Button } from "../shared/Button";
import { entregas as api } from "../../api/modulos.service";
import { estilos as est, Cabecera, Vacio, Aviso, Entrada, Seleccion, Area, useCarga } from "./base";

/**
 * ENTREGAS — la misma pregunta desde los dos lados.
 *
 * El residente decide una vez qué pasa con sus entregas. El guardia, con el
 * repartidor delante, escribe la casa y lee la respuesta. Entre esas dos
 * pantallas desaparece la llamada telefónica que hoy se hace varias veces al
 * día y que, cuando nadie contesta, termina con el repartidor yéndose.
 *
 * Por eso la pantalla de la caseta enseña **una instrucción**, no datos para
 * interpretar: qué hacer, por qué, y el teléfono por si aun así hay que
 * llamar.
 */

const COLOR = {
  recepcion: "var(--gold)",
  casa: "var(--success)",
  no_permitir: "var(--danger)",
  una_vez: "var(--warning)",
};

/**
 * ── El lado del residente ──────────────────────────────────────────────
 *
 * El administrador entra aquí también, pero él **no tiene casa propia**: la
 * suya es la administración. Por eso, cuando es admin, primero elige de qué
 * casa habla. Sin eso la pantalla pedía la preferencia «de su casa», el
 * servidor respondía 400 porque no hay tal casa, y quedaba en «Cargando…»
 * para siempre. Es exactamente el fallo que encontró la prueba con navegador.
 *
 * Y sirve para algo real: hay quien llama a la administración en vez de entrar
 * al sistema, y alguien tiene que poder anotarlo por él.
 */
function MiPreferencia({ rol }) {
  const esAdmin = rol === "admin";
  const [casa, setCasa] = useState("");
  const [poligono, setPoligono] = useState("");
  const [consultada, setConsultada] = useState(esAdmin ? null : {});
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [listo, setListo] = useState(false);
  const [form, setForm] = useState(null);

  const { datos, cargando, error: errorCarga, recargar } = useCarga(
    () => (consultada ? api.verPreferencia(consultada) : Promise.resolve(null)),
    [consultada]
  );

  React.useEffect(() => {
    if (datos?.preferencia) {
      setForm({
        handling: datos.preferencia.handling,
        instructions: datos.preferencia.instructions || "",
        horas: 12,
        porEmpresa: datos.preferencia.porEmpresa || [],
      });
    }
  }, [datos]);

  const elegirCasa = (e) => {
    e?.preventDefault?.();
    if (!casa.trim()) return;
    setForm(null);
    setListo(false);
    setConsultada({ casa: casa.trim(), poligono: poligono.trim() });
  };

  const selector = esAdmin && (
    <form onSubmit={elegirCasa} style={est.tarjeta}>
      <h3 style={est.tarjetaTitulo}>¿De qué casa?</h3>
      <div style={est.tarjetaDato}>
        Usted administra: elija la casa cuya preferencia va a ver o cambiar.
      </div>
      <div style={est.fila}>
        <Entrada etiqueta="Casa" value={casa} onChange={(e) => setCasa(e.target.value)} required />
        <Entrada etiqueta="Polígono" value={poligono} onChange={(e) => setPoligono(e.target.value)} />
      </div>
      <div style={est.tarjetaAcciones}>
        <Button variant="primary" onClick={elegirCasa}>
          Ver preferencia
        </Button>
      </div>
    </form>
  );

  if (!consultada) {
    return (
      <div style={{ display: "grid", gap: "var(--space-4)" }}>
        {selector}
        <Vacio>Escriba una casa para ver qué hacer con sus entregas.</Vacio>
      </div>
    );
  }

  /* El error se enseña. Antes, si la petición fallaba, `cargando` pasaba a
   * false pero `form` seguía en null y la pantalla se quedaba diciendo
   * «Cargando…» sin que nada estuviera cargando. */
  if (errorCarga) {
    return (
      <div style={{ display: "grid", gap: "var(--space-4)" }}>
        {selector}
        <Aviso>{errorCarga}</Aviso>
      </div>
    );
  }

  if (cargando || !form) {
    return (
      <div style={{ display: "grid", gap: "var(--space-4)" }}>
        {selector}
        <Vacio>Cargando…</Vacio>
      </div>
    );
  }

  const opciones = datos?.opciones || [];
  const empresas = datos?.empresas || [];

  const guardar = async () => {
    setError("");
    setListo(false);
    setGuardando(true);
    try {
      await api.guardarPreferencia({ ...form, ...(esAdmin ? consultada : {}) });
      setListo(true);
      recargar();
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudo guardar");
    } finally {
      setGuardando(false);
    }
  };

  const regla = (empresa) => (form.porEmpresa.find((r) => r.company === empresa) || {}).handling || "";

  const cambiarRegla = (empresa) => (e) => {
    const valor = e.target.value;
    const otras = form.porEmpresa.filter((r) => r.company !== empresa);
    setForm({ ...form, porEmpresa: valor ? [...otras, { company: empresa, handling: valor }] : otras });
  };

  return (
    <div style={{ display: "grid", gap: "var(--space-4)" }}>
      {selector}
      {error && <Aviso>{error}</Aviso>}
      {listo && (
        <div style={{ ...est.tarjetaDato, color: "var(--success)" }}>
          <Check size={16} /> Guardado. El guardia ya lo ve.
        </div>
      )}

      <div style={est.tarjeta}>
        <h3 style={est.tarjetaTitulo}>
          {esAdmin ? `Entregas de la casa ${consultada.casa}` : "¿Qué hacemos con sus entregas?"}
        </h3>
        <div style={est.tarjetaDato}>
          Esto es lo que el guardia va a leer cuando llegue un repartidor a su casa.
        </div>

        <div style={{ display: "grid", gap: "var(--space-2)", marginTop: "var(--space-3)" }}>
          {opciones.map((o) => (
            <button
              key={o.valor}
              type="button"
              onClick={() => setForm({ ...form, handling: o.valor })}
              style={{
                minHeight: "44px",
                padding: "var(--space-3)",
                textAlign: "left",
                cursor: "pointer",
                borderRadius: "var(--radius-lg)",
                border: `2px solid ${form.handling === o.valor ? COLOR[o.valor] : "var(--border)"}`,
                backgroundColor: form.handling === o.valor ? "var(--bg-tertiary)" : "transparent",
                color: "var(--text)",
                fontSize: "var(--text-base)",
              }}
              aria-pressed={form.handling === o.valor}
            >
              {o.etiqueta}
            </button>
          ))}
        </div>

        {/* «Sólo esta vez» sin fecha de vencimiento es un permiso permanente
            con otro nombre; por eso aquí se pregunta cuántas horas. */}
        {form.handling === "una_vez" && (
          <Entrada
            etiqueta="¿Por cuántas horas?"
            type="number"
            min="1"
            max="72"
            value={form.horas}
            onChange={(e) => setForm({ ...form, horas: e.target.value })}
          />
        )}

        <Area
          etiqueta="Instrucciones para el repartidor (opcional)"
          value={form.instructions}
          onChange={(e) => setForm({ ...form, instructions: e.target.value })}
          placeholder="Timbre de la derecha"
        />
      </div>

      <div style={est.tarjeta}>
        <h3 style={est.tarjetaTitulo}>Excepciones por empresa</h3>
        <div style={est.tarjetaDato}>
          Sólo si quiere algo distinto para alguna. Lo que deje en blanco sigue la regla de arriba.
        </div>
        <div style={{ display: "grid", gap: "var(--space-2)", marginTop: "var(--space-3)" }}>
          {empresas.map((emp) => (
            <Seleccion
              key={emp}
              etiqueta={emp}
              value={regla(emp)}
              onChange={cambiarRegla(emp)}
              opciones={[{ valor: "", etiqueta: "Como todas" }, ...opciones]}
            />
          ))}
        </div>
      </div>

      <Button variant="primary" onClick={guardar} loading={guardando}>
        Guardar
      </Button>
    </div>
  );
}

/** ── El lado de la caseta ───────────────────────────────────────────── */
function Consulta() {
  const [casa, setCasa] = useState("");
  const [poligono, setPoligono] = useState("");
  const [empresa, setEmpresa] = useState("");
  const [resultado, setResultado] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [error, setError] = useState("");

  const consultar = async (e) => {
    e?.preventDefault?.();
    if (!casa.trim()) return;
    setError("");
    setBuscando(true);
    try {
      setResultado(await api.consultar({ casa: casa.trim(), poligono: poligono.trim(), empresa }));
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudo consultar");
      setResultado(null);
    } finally {
      setBuscando(false);
    }
  };

  return (
    <div style={{ display: "grid", gap: "var(--space-4)" }}>
      <form onSubmit={consultar} style={est.tarjeta}>
        <h3 style={est.tarjetaTitulo}>Llegó una entrega</h3>
        <div style={est.fila}>
          <Entrada etiqueta="Casa" value={casa} onChange={(e) => setCasa(e.target.value)} required />
          <Entrada etiqueta="Polígono" value={poligono} onChange={(e) => setPoligono(e.target.value)} />
        </div>
        <Seleccion
          etiqueta="Empresa"
          value={empresa}
          onChange={(e) => setEmpresa(e.target.value)}
          opciones={[
            { valor: "", etiqueta: "Cualquiera" },
            ...["Uber Eats", "PedidosYa", "Hugo", "Amazon", "DHL", "FedEx", "UPS", "Correos", "Supermercado", "Mensajería", "Otro"],
          ]}
        />
        <div style={est.tarjetaAcciones}>
          <Button variant="primary" leftIcon={Search} loading={buscando} onClick={consultar}>
            Consultar
          </Button>
        </div>
      </form>

      {error && <Aviso>{error}</Aviso>}

      {resultado && (
        <div style={{ ...est.tarjeta, borderLeft: `4px solid ${COLOR[resultado.handling] || "var(--border)"}` }}>
          <h3 style={{ ...est.tarjetaTitulo, fontSize: "var(--text-lg)", color: COLOR[resultado.handling] }}>
            {resultado.etiqueta}
          </h3>
          <div style={est.tarjetaDato}>{resultado.porque}</div>

          {resultado.instructions && (
            <div style={{ ...est.tarjetaDato, display: "block", color: "var(--text)" }}>
              <strong>Instrucciones: </strong>
              {resultado.instructions}
            </div>
          )}

          {resultado.avisar && <Aviso>Confirme con el residente antes de decidir.</Aviso>}

          {(resultado.residentes || []).length > 0 && (
            <div style={{ display: "grid", gap: "var(--space-2)", marginTop: "var(--space-2)" }}>
              {resultado.residentes.map((r, i) => (
                <div key={i} style={est.tarjetaDato}>
                  <Phone size={14} /> {r.name}
                  {r.phone ? (
                    <a href={`tel:${r.phone}`} style={{ color: "var(--gold)", marginLeft: "var(--space-2)" }}>
                      {r.phone}
                    </a>
                  ) : (
                    " · sin teléfono"
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Entregas({ rol }) {
  const esCaseta = rol === "guard" || rol === "admin";
  const esResidente = rol === "resident" || rol === "admin";
  /* El administrador abre en la consulta de caseta, no en «mi preferencia»:
   * él no tiene casa propia, y lo que hace a diario es resolver la duda de un
   * guardia o anotar lo que un vecino pidió por teléfono. */
  const [pestana, setPestana] = useState(rol === "resident" ? "mia" : "caseta");

  return (
    /* `minWidth: 0`, por lo mismo que en «Llegó alguien»: la pantalla se monta
       como celda de una rejilla y una celda arranca con `min-width: auto`, así
       que adopta el ancho de su hijo más rígido en vez del que le toca. Aquí
       los hijos rígidos son botones —los de abajo y los que vienen con las
       opciones del servidor—, y cualquiera que pida más de la cuenta se lleva
       la página entera con él. */
    <div style={{ ...est.seccion, minWidth: 0 }}>
      <Cabecera
        titulo="Entregas"
        subtitulo="Decidido una vez, para no tener que llamar cada vez."
        icono={Caja}
      />

      {/* El administrador ve las dos cosas; los demás, la suya.
          `flexWrap: "wrap"` porque estas dos pestañas no caben en una línea de
          teléfono: medidas con el tipo del sistema piden 201 px y 245 px, 454
          con el hueco, contra los 366 que hay a 390 px de pantalla. Sin el
          salto de línea la fila no se encogía y arrastraba toda la pantalla al
          desborde horizontal. */}
      {esCaseta && esResidente && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)" }}>
          {[
            { id: "mia", etiqueta: "Mi preferencia" },
            { id: "caseta", etiqueta: "Consulta de caseta" },
          ].map((p) => (
            <Button
              key={p.id}
              size="sm"
              variant={pestana === p.id ? "primary" : "outline"}
              onClick={() => setPestana(p.id)}
            >
              {p.etiqueta}
            </Button>
          ))}
        </div>
      )}

      {pestana === "caseta" && esCaseta ? (
        <Consulta />
      ) : esResidente ? (
        <MiPreferencia rol={rol} />
      ) : (
        <Consulta />
      )}
    </div>
  );
}
